import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '../../../utils/cn';

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  className,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value);
    }
  }, [value, isEditing]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPicker]);

  const handleColorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setLocalValue(newColor);
    onChange(newColor);
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    // Ensure it starts with #
    if (!val.startsWith('#')) {
      val = '#' + val;
    }
    setLocalValue(val);
  };

  const handleHexInputBlur = () => {
    setIsEditing(false);
    // Validate hex color
    const hexRegex = /^#([0-9A-Fa-f]{3}){1,2}$/;
    if (hexRegex.test(localValue)) {
      // Normalize to 6 digit hex
      let normalized = localValue;
      if (localValue.length === 4) {
        normalized = '#' + localValue[1] + localValue[1] + localValue[2] + localValue[2] + localValue[3] + localValue[3];
      }
      onChange(normalized.toLowerCase());
      setLocalValue(normalized.toLowerCase());
    } else {
      setLocalValue(value);
    }
  };

  const handleHexInputFocus = () => {
    setIsEditing(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setLocalValue(value);
      setIsEditing(false);
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleSwatchClick = useCallback(() => {
    if (!disabled) {
      colorInputRef.current?.click();
    }
  }, [disabled]);

  return (
    <div ref={containerRef} className={cn('flex flex-col gap-1', className)}>
      {label && (
        <label className="text-xs text-zinc-400 select-none">{label}</label>
      )}
      <div className="flex items-center gap-2">
        {/* Color swatch */}
        <button
          onClick={handleSwatchClick}
          disabled={disabled}
          className={cn(
            'w-8 h-8 rounded border border-zinc-600 cursor-pointer',
            'hover:border-zinc-500 transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'relative overflow-hidden'
          )}
          style={{ backgroundColor: value }}
          title="Click to pick color"
        >
          {/* Checkerboard pattern for transparency preview */}
          <div
            className="absolute inset-0 -z-10"
            style={{
              backgroundImage: `
                linear-gradient(45deg, #404040 25%, transparent 25%),
                linear-gradient(-45deg, #404040 25%, transparent 25%),
                linear-gradient(45deg, transparent 75%, #404040 75%),
                linear-gradient(-45deg, transparent 75%, #404040 75%)
              `,
              backgroundSize: '8px 8px',
              backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
            }}
          />
        </button>

        {/* Hidden native color input */}
        <input
          ref={colorInputRef}
          type="color"
          value={value}
          onChange={handleColorInputChange}
          disabled={disabled}
          className="sr-only"
        />

        {/* Hex input */}
        <input
          type="text"
          value={localValue}
          onChange={handleHexInputChange}
          onBlur={handleHexInputBlur}
          onFocus={handleHexInputFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="#ffffff"
          className={cn(
            'flex-1 px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded',
            'text-zinc-100 font-mono focus:outline-none focus:border-blue-500',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        />
      </div>
    </div>
  );
};
